<script lang="ts">
    import dayjs from 'dayjs';
    import { createDatePicker, createTabs } from '@melt-ui/svelte';
    import { parseDate, DateValue } from '@internationalized/date';
    import {
        VpCalendarIcon,
        VpReturnIcon,
    } from '@decathlon-experience/icons/svelte';

    import CalendarPicker from '$saga/components/CalendarPicker.svelte';
    import Button from '$saga/components/Button.svelte';
    import cn from '$common/utils/cn';
    import { filtersContext } from '$saga/views/Search/store';
    import FlexibilityFilter from './FlexibilityFilter.svelte';

    export let startDateValue: DateValue | undefined;
    export let endDateValue: string | undefined;
    export let flexibility = 0;
    export let handleValidateSelection: (value: {
        startDate: DateValue | undefined;
        endDate: Date | undefined;
        flexibility: number;
    }) => void;

    const {
        elements: {
            calendar,
            cell,
            grid,
            heading,
            nextButton,
            prevButton,
            content,
        },
        states: { value, months, headingValue, weekdays },
        helpers: { isDateDisabled, isDateUnavailable },
    } = createDatePicker({
        locale: 'fr',
        minValue: parseDate(dayjs().add(1, 'day').format('YYYY-MM-DD')),
        // if endDateValue is present, then we chose a month
        defaultValue: endDateValue == null ? startDateValue : undefined,
        forceVisible: true,
        numberOfMonths: 2,
        onValueChange: (dateValue) => {
            // Clear endDate when switching to specific date selection (date-selection)
            // This ensures month selection is cleared when user picks a specific date
            if ($valueTabs === 'date-selection') {
                $filtersContext.formValues.endDate = undefined;
            }

            return dateValue.next;
        },
    });

    const {
        elements: { root, list, content: contentTabs, trigger },
        states: { value: valueTabs },
    } = createTabs({
        defaultValue: 'date-selection',
    });
    const triggers = [
        { id: 'date-selection', title: 'Dates de départ' },
        { id: 'month-selection', title: 'Mois de départ' },
    ];

    // set the initial value of valueTabs based on endDateValue
    $: {
        if (endDateValue != null) $valueTabs = 'month-selection';
    }

    // remote validate the selection (e.g dialog is closed from outside click)
    export const validateSelection = () => {
        if ($valueTabs === 'month-selection' && endDateValue) {
            // Month selection mode
            handleValidateSelection({
                startDate: startDateValue,
                endDate: typeof endDateValue === 'string'
                    ? dayjs(endDateValue).endOf('month').toDate()
                    : endDateValue,
                flexibility,
            });
        } else {
            // Date selection mode
            handleValidateSelection({
                startDate: $value,
                endDate: undefined,
                flexibility,
            });
        }
    };
</script>

<div class="flex h-full w-full flex-col">
    <div {...$root} use:root class={cn('mx-auto mt-s w-fit')}>
        <div
            {...$list}
            use:list
            class="flex gap-m rounded-full bg-container-quiet p-xs"
        >
            {#each triggers as triggerItem}
                <button
                    {...$trigger}
                    use:trigger
                    class={cn(
                        'full rounded-full px-xs py-2xs text-xs text-content-brand transition-colors duration-300',
                        $valueTabs === triggerItem.id &&
                            'bg-container-brand text-white',
                    )}
                    on:click={() => ($valueTabs = triggerItem.id)}
                >
                    {triggerItem.title}
                </button>
            {/each}
        </div>
    </div>

    <div {...$contentTabs('date-selection')} use:contentTabs class="h-full">
        <div {...content} use:content class="flex h-full w-full flex-col">
            <span class="my-m w-full px-m text-center">
                Sélectionnez une date de départ
            </span>
            <CalendarPicker
                class="mx-auto h-full"
                {calendar}
                {prevButton}
                {heading}
                {headingValue}
                {nextButton}
                {months}
                {grid}
                {weekdays}
                {isDateDisabled}
                {isDateUnavailable}
                {cell}
                monthsOrientation="vertical"
                isMultipleMonths
            />
        </div>
    </div>

    <div {...$contentTabs('month-selection')} use:contentTabs class="h-full">
        <span class="my-m inline-block w-full px-m text-center text-sm">
            Sélectionnez un mois de départ
        </span>
        <div class="mx-auto grid h-fit w-fit grid-cols-3 gap-2xl">
            <!--    display the 12 next months as a button -->
            {#each Array(12) as _, index}
                {@const month = dayjs().add(index, 'month')}
                <button
                    class={cn(
                        'flex size-[100px] flex-col items-center justify-center gap-xs rounded-xl border border-solid border-border-neutral text-sm',
                        'hover:border-border-brand hover:bg-container-commercial-neutral hover:text-content-brand',
                        endDateValue != null &&
                            startDateValue &&
                            dayjs(startDateValue?.toString()).get('month') ===
                                month.get('month') &&
                            'border-border-brand bg-container-brand text-white',
                    )}
                    on:click={() => {
                        // set formValues.startDate and formValues.endDate to the first day of the month and last day of the month: if the month is in the past, then select the month the next year
                        const startDate = month.startOf('month');
                        const endDate = month.endOf('month');
                        handleValidateSelection({
                            startDate: parseDate(
                                startDate.format('YYYY-MM-DD'),
                            ),
                            endDate: endDate.toDate(),
                            flexibility,
                        });
                    }}
                >
                    <VpCalendarIcon class="size-xl" />
                    {month.format('MMM YY')}
                </button>
            {/each}
        </div>
    </div>

    <div class="sticky bottom-0 w-full bg-white">
        {#if $valueTabs === 'date-selection'}
            <FlexibilityFilter bind:flexibility />
        {/if}

        <div
            class="flex w-full justify-between border-t border-solid border-border-quiet py-s"
        >
            <Button
                size="small"
                intent="secondary"
                shape="rounded"
                type="button"
                on:click={() => {
                    handleValidateSelection({
                        startDate: undefined,
                        endDate: undefined,
                        flexibility: 0,
                    });
                    $value = undefined;
                }}
                startIcon={VpReturnIcon}>Effacer</Button
            >
            <Button
                size="small"
                shape="rounded"
                type="button"
                on:click={() => {
                    if ($valueTabs === 'month-selection' && endDateValue) {
                        // Month selection mode - keep the month selection
                        handleValidateSelection({
                            startDate: startDateValue,
                            endDate: typeof endDateValue === 'string'
                                ? dayjs(endDateValue).endOf('month').toDate()
                                : endDateValue,
                            flexibility,
                        });
                    } else {
                        // Date selection mode - use picked date
                        handleValidateSelection({
                            startDate: $value,
                            endDate: undefined,
                            flexibility,
                        });
                    }
                }}
            >
                Appliquer
            </Button>
        </div>
    </div>
</div>
